// SAB-401: NFL mobile player card. Mirrors
// mlb-beta/players-mobile/PlayerCardMobile.tsx structurally — same layout,
// spacing, RankCoin, OwnershipBadge, InjuryPill, Add button — but adapted
// for NFL stat shape:
//
//   - Position colors come from the NFL_POSITION_COLORS map below
//     (vs. mlb-beta/MLBPlayerAvatar's positionColors map). NFL uses a
//     different palette because the offense/defense split needs more
//     visual differentiation than MLB hitters/pitchers.
//   - Stat strip shows Sabotage breakdown (TRASH / ANTI / NET) instead of
//     baseball box-score columns. Trash > 0 + Anti < 0 is the inverted
//     scoring thesis from the brief.
//   - Projections row (proj trash / anti / total + ADP) shown when
//     available — surfaces nfl_players.projected_* values per the brief.
//   - DEF rendering is NOVEL — guarded with a Shield avatar placeholder
//     and a "DEF rendering polish — separate ticket" comment. Don't crash,
//     don't design the treatment.
import { memo } from 'react';
import { NFLWaiverBadge } from '@/components/nfl/NFLWaiverBadge';
import { isOnWaivers } from '@/lib/nflWaiverBadge';
import { AvailabilityPill } from '../PlayerAvailability';
import { ChevronDown, Shield, Plus, MinusCircle, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { fmtInt } from '@/components/nfl/nflStatsDefs';

export interface NFLPlayerCardData {
  id: string;
  full_name: string;
  position: string;
  nfl_team_abbr: string | null;
  headshot_url: string | null;
  // SAB-749: ROSTER availability — ACT / IR / PUP / NON / EXE / UFA. This is the
  // field with live data. `injury_status` below is the weekly injury-report
  // designation (Out / Questionable / Doubtful) and is NULL for every player
  // until REG week 1, because SportRadar publishes no preseason injury report.
  status: string;
  injury_status: string | null;
  // SAB-1033: false = not on any current NFL roster. Optional because callers that build
  // this shape by hand (the add/drop modals) do not carry it; absent means "no claim made",
  // which renders nothing rather than asserting he IS rostered.
  active_2026?: boolean | null;
  games_played: number;
  total_sabotage: number;
  total_trash: number;
  total_anti: number;
  avg_sabotage: number;
  // SAB-1176: the six stat keys, from the same server aggregate the desktop table sorts on.
  total_yds: number;
  total_td: number;
  total_int: number;
  total_fum: number;
  total_drp: number;
  total_tfl: number;
  owner_team_name: string | null;
  // SAB-1045: server-derived waiver designation. The client never computes these --
  // get_nfl_player_leaderboard reads the same nfl_waiver_settings() that nfl-add-drop's
  // refusal reads, so the badge and the refusal cannot disagree.
  waiver_state?: 'post_draft' | 'dropped' | null;
  waiver_clears_at?: string | null;
  // Optional, sourced from supplementary nfl_players select. Null when
  // projections haven't been generated yet for that player.
  projected_trash_points: number | null;
  projected_anti_points: number | null;
  projected_total_points: number | null;
  adp: number | null;
  // SAB-811: the SHIPPED board total — what the draft actually ranks on. The
  // projected_* trio above is an older, different set (correlates 0.657 with this).
  // Kept, not retired: projected_anti_points is the only route trash-optimal has
  // left to reach IDP. Nullable on purpose; render a dash, never 0.0 (SAB-809).
  proj_sab_pts: number | null;
  proj_source: string | null;
}

interface PlayerCardMobileProps {
  player: NFLPlayerCardData;
  rank: number;
  isExpanded: boolean;
  isOnMyTeam: boolean;
  isAddPending: boolean;
  isDropPending: boolean;
  onCardClick: () => void;
  onToggleExpanded: () => void;
  onAddClick: (p: NFLPlayerCardData) => void;
  /** SAB-1024: false pre-draft — the Add affordance is withheld, browsing is not. */
  canAcquire?: boolean;
  onDropClick: (p: NFLPlayerCardData) => void;
}

// NFL position color treatments. Keep the keys aligned with
// NFL_POSITION_FILTERS so a chip always renders even for novel positions.
// DEF/DST gets the muted slate placeholder — full styling deferred per brief.
const NFL_POSITION_COLORS: Record<string, string> = {
  QB:    'border-red-500/40 text-red-400 bg-red-500/10',
  RB:    'border-emerald-500/40 text-emerald-400 bg-emerald-500/10',
  WR:    'border-blue-500/40 text-blue-400 bg-blue-500/10',
  TE:    'border-orange-500/40 text-orange-400 bg-orange-500/10',
  K:     'border-purple-500/40 text-purple-400 bg-purple-500/10',
  P:     'border-violet-500/40 text-violet-400 bg-violet-500/10',
  DST:   'border-slate-500/40 text-slate-300 bg-slate-500/10',
  COACH: 'border-amber-500/40 text-amber-400 bg-amber-500/10',
  DB:    'border-cyan-500/40 text-cyan-400 bg-cyan-500/10',
  LB:    'border-teal-500/40 text-teal-400 bg-teal-500/10',
  DL:    'border-indigo-500/40 text-indigo-400 bg-indigo-500/10',
};

function OwnershipBadge({ ownerTeamName, isOnMyTeam }: { ownerTeamName: string | null; isOnMyTeam: boolean }) {
  if (isOnMyTeam) {
    return (
      <Badge
        variant="secondary"
        className="text-[9px] px-1.5 h-4 bg-primary/15 text-primary border-primary/20 shrink-0"
      >
        My Team
      </Badge>
    );
  }
  if (ownerTeamName) {
    return (
      <Badge variant="outline" className="text-[9px] px-1.5 h-4 text-muted-foreground shrink-0 max-w-[120px] truncate">
        {ownerTeamName}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[9px] px-1.5 h-4 text-success border-success/30 shrink-0">
      FA
    </Badge>
  );
}

function RankCoin({ rank }: { rank: number }) {
  let cls = 'text-muted-foreground/60';
  if (rank <= 3) cls = 'text-gold font-extrabold';
  else if (rank <= 10) cls = 'text-primary font-bold';
  else if (rank <= 50) cls = 'text-muted-foreground font-semibold';
  return (
    <span className={cn('w-7 text-right font-display tabular-nums text-[13px]', cls)}>
      #{rank}
    </span>
  );
}

// DEF rendering is NOVEL (NOVEL_SURFACES #4). Render a Shield placeholder
// + the position text; full team-logo + stat-bracket treatment deferred to
// a separate Adam-approved ticket per the SAB-401 brief. The size matches
// the headshot placement so the row height stays uniform.
function PlayerAvatar({ player }: { player: NFLPlayerCardData }) {
  const isDef = player.position === 'DST';
  if (isDef) {
    return (
      <div
        aria-label="Defense placeholder"
        className="w-9 h-9 rounded-full bg-slate-500/15 border border-slate-500/30 flex items-center justify-center shrink-0"
      >
        <Shield className="w-4 h-4 text-slate-300" />
      </div>
    );
  }
  if (player.headshot_url) {
    return (
      <img
        src={player.headshot_url}
        alt={player.full_name}
        className="w-9 h-9 rounded-full object-cover bg-muted shrink-0"
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
    );
  }
  const initials = player.full_name
    .split(' ')
    .map(n => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div className="w-9 h-9 rounded-full bg-muted border border-border flex items-center justify-center shrink-0">
      <span className="text-[10px] font-semibold text-muted-foreground">{initials}</span>
    </div>
  );
}

function StatStrip({ player }: { player: NFLPlayerCardData }) {
  // SAB-811: keyed to what is now DISPLAYED (board total + ADP), not to the older
  // projected_* trio. Keying it to columns the strip no longer shows would hide the
  // projection row for a player who has a board value but no legacy projection.
  const hasProjections = (player.proj_sab_pts !== null || player.adp !== null);
  return (
    <div className="px-3 pb-3 pt-1 border-t border-border/40 grid grid-cols-4 gap-2 text-center">
      <StatCell label="Trash"  value={player.total_trash.toFixed(1)} tone="trash" />
      <StatCell label="Anti"   value={player.total_anti.toFixed(1)}  tone="anti" />
      <StatCell label="Avg/G"  value={player.avg_sabotage.toFixed(1)} tone="muted" />
      <StatCell label="GP"     value={String(player.games_played)} tone="muted" />
      {/* SAB-1176: the six stat keys on their own row below a divider, per the handoff's
          mobile card. Zeros render as a dash via fmtInt's dash-AFTER-rounding rule, so a
          value that rounds away is not printed as a measured 0. */}
      <div className="col-span-4 mt-1 grid grid-cols-6 gap-1 border-t border-border/40 pt-2">
        <StatCell label="YDS" value={fmtInt(player.total_yds)} tone="anti" />
        <StatCell label="TD"  value={fmtInt(player.total_td)}  tone="anti" />
        <StatCell label="INT" value={fmtInt(player.total_int)} tone="trash" />
        <StatCell label="FUM" value={fmtInt(player.total_fum)} tone="trash" />
        <StatCell label="DRP" value={fmtInt(player.total_drp)} tone="trash" />
        <StatCell label="TFL" value={fmtInt(player.total_tfl)} tone="trash" />
      </div>
      {hasProjections && (
        <>
          {/* SAB-811: was Proj T / Proj A / Proj N — an OLDER projection set that
              correlates 0.657 with the board users actually draft against. Replaced by the
              single shipped total, per Adam: the number a user sees should be the number
              that orders their draft. The projected_* columns are NOT retired —
              projected_anti_points is the only route trash-optimal has left to reach IDP —
              they are just no longer what this surface shows. */}
          <StatCell label="Proj"   value={fmtOptional(player.proj_sab_pts, 1)} tone="muted" subtle />
          <StatCell label="ADP"    value={fmtOptional(player.adp, 0)} tone="muted" subtle />
        </>
      )}
    </div>
  );
}

function fmtOptional(v: number | null, digits: number): string {
  if (v === null || v === undefined) return '-';
  return v.toFixed(digits);
}

function StatCell({ label, value, tone, subtle }: { label: string; value: string; tone: 'trash' | 'anti' | 'muted'; subtle?: boolean }) {
  const toneClass =
    tone === 'trash' ? 'text-primary' :
    tone === 'anti'  ? 'text-damage' :
                       'text-foreground';
  return (
    <div>
      <div className={cn('font-display font-bold tabular-nums leading-tight', subtle ? 'text-[12px] opacity-70' : 'text-[13px]', toneClass)}>
        {value}
      </div>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-display font-semibold">
        {label}
      </div>
    </div>
  );
}

function PlayerCardMobileImpl({
  player, rank, isExpanded,
  isOnMyTeam, isAddPending, isDropPending,
  onCardClick, onToggleExpanded,
  onAddClick, onDropClick, canAcquire = true,
}: PlayerCardMobileProps) {
  const isTopTen = rank <= 10;
  const sabValue = player.total_sabotage;

  return (
    <div
      onClick={onCardClick}
      className={cn(
        'bg-card border rounded-xl overflow-hidden transition-colors cursor-pointer',
        'border-border hover:border-primary/30',
      )}
    >
      <div className="flex items-center gap-2 px-2.5 py-1.5 min-h-[52px]">
        <RankCoin rank={rank} />
        <PlayerAvatar player={player} />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-bold text-[14.5px] truncate text-foreground">
              {player.full_name}
            </span>
            {/*
              SAB-964: ONE marker, from the shared resolver, replacing the two badges that
              used to sit here. The local InjuryPill read `injury_status` only -- NULL for
              all 3,328 players until REG week 1 -- so it had never rendered once, and from
              week 1 it would have started doubling up with the roster badge beside it.
            */}
            <AvailabilityPill injuryStatus={player.injury_status} status={player.status} />
            {/*
              SAB-1033. Separate from AvailabilityPill deliberately: that answers "can he
              play this week", this answers "is he on an NFL roster at all". Before the
              ungating a cut player answered neither — he was absent from the result set,
              so hidden and nonexistent looked the same, even for a player someone owned.
            */}
            {player.active_2026 === false && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground whitespace-nowrap"
                title="Not on a current NFL roster. He can still be dropped, and stays on your roster until you do."
              >
                Not on an NFL roster
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
            <Badge
              variant="outline"
              className={cn(
                'text-[9.5px] px-1 py-0 h-3.5 shrink-0 min-w-[28px] text-center justify-center',
                NFL_POSITION_COLORS[player.position] ?? 'border-border text-muted-foreground',
              )}
            >
              {player.position}
            </Badge>
            <span className="text-[10.5px] font-display font-semibold text-muted-foreground shrink-0">
              {player.nfl_team_abbr || 'FA'}
            </span>
            <OwnershipBadge ownerTeamName={player.owner_team_name} isOnMyTeam={isOnMyTeam} />
            {/* SAB-811: the shipped board, on the COLLAPSED face. StatStrip below only
                renders when a card is expanded, and projections you have to tap for are
                not usable while drafting. Dash for null, never 0.0 — IDP carry no
                projection for year one and 86 board players have no basis (SAB-809). */}
            {/* One flex item, not two: the meta row wraps, and as separate items ADP
                orphans onto its own line while PROJ stays up top. Kept together they
                wrap as a pair and read as one unit. */}
            <span className="text-[10.5px] font-display font-semibold text-muted-foreground shrink-0 tabular-nums whitespace-nowrap">
              PROJ{' '}
              <span className={player.proj_sab_pts == null ? 'text-muted-foreground/50' : 'text-foreground'}>
                {fmtOptional(player.proj_sab_pts, 1)}
              </span>
              <span className="mx-1 text-muted-foreground/40">·</span>
              ADP{' '}
              <span className={player.adp == null ? 'text-muted-foreground/50' : 'text-foreground'}>
                {fmtOptional(player.adp, 0)}
              </span>
            </span>
          </div>
        </div>

        <div className="flex flex-col items-end shrink-0">
          <span
            className={cn(
              'font-display font-extrabold text-[18px] text-primary tabular-nums leading-none',
              isTopTen && 'drop-shadow-[0_0_12px_hsl(25_95%_53%/0.45)]',
            )}
          >
            {sabValue.toFixed(1)}
          </span>
          <span className="text-[8.5px] uppercase tracking-wider text-muted-foreground font-display font-semibold mt-0.5">
            SAB
          </span>
          <div className="mt-0.5" onClick={(e) => e.stopPropagation()}>
            {isOnMyTeam ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onDropClick(player)}
                disabled={isDropPending}
                className="h-6 px-1.5 text-[10px] text-muted-foreground hover:text-damage"
                aria-label={`Drop ${player.full_name}`}
              >
                {isDropPending
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <MinusCircle className="w-3 h-3" />}
              </Button>
            ) : !canAcquire ? (
              // Nothing rendered rather than a disabled button: a greyed Add invites a tap
              // and explains nothing. The list-level banner carries the reason once.
              null
            ) : isOnWaivers(player) ? (
              // SAB-1045: replaces the plus. Tapping still opens the same acquisition
              // flow -- bid mode is chosen by the server's refusal, not decided here.
              <NFLWaiverBadge player={player} onClick={() => onAddClick(player)} />
            ) : (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onAddClick(player)}
                disabled={isAddPending}
                className="h-6 px-1.5 text-[10px] text-primary hover:bg-primary/10"
                aria-label={`Add ${player.full_name}`}
              >
                {isAddPending
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <Plus className="w-3 h-3" />}
              </Button>
            )}
          </div>
        </div>
      </div>

      {isExpanded && <StatStrip player={player} />}

      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleExpanded(); }}
        aria-label={isExpanded ? 'Collapse stats' : 'Expand stats'}
        aria-expanded={isExpanded}
        className="w-full flex items-center justify-center py-0.5 border-t border-border/40 hover:bg-muted/20"
      >
        <ChevronDown
          className={cn(
            'w-4 h-4 text-muted-foreground transition-transform duration-150',
            isExpanded && 'rotate-180',
          )}
        />
      </button>
    </div>
  );
}

export const PlayerCardMobile = memo(PlayerCardMobileImpl);
export default PlayerCardMobile;
